import { PublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser';

// Vite environment variables type declaration
declare global {
  interface ImportMeta {
    env: Record<string, string>;
  }
}

// Determine redirect URI: prioritize environment variable, then use current origin
// This ensures production uses the correct domain instead of localhost
// Note: The redirect URI must match EXACTLY what's registered in Azure AD (including trailing slash)
// IMPORTANT: Do NOT modify the redirect URI from environment - use it exactly as configured
const getRedirectUri = (): string => {
  const envRedirectUri = import.meta.env.VITE_AZURE_AD_REDIRECT_URI || import.meta.env.VITE_ENTRA_EXTERNAL_ID_REDIRECT_URI;
  if (envRedirectUri) {
    // Use the redirect URI exactly as configured - DO NOT add/remove trailing slashes
    // Azure AD is very strict about exact matching
    const redirectUri = envRedirectUri.trim();
    console.log('[AuthConfig] Using redirect URI from environment:', redirectUri);
    console.log('[AuthConfig] Current window location:', window.location.origin + window.location.pathname);
    
    // Warn if there's a mismatch (common issue)
    const currentUrl = window.location.origin + (window.location.pathname || '/');
    
    // Normalize both for comparison (remove trailing slashes for comparison)
    const normalizedCurrent = currentUrl.replace(/\/$/, '');
    const normalizedRedirect = redirectUri.replace(/\/$/, '');
    
    if (normalizedCurrent !== normalizedRedirect) {
      console.warn('[AuthConfig] ⚠️ Redirect URI mismatch detected!');
      console.warn('[AuthConfig]   Configured:', redirectUri);
      console.warn('[AuthConfig]   Current URL:', currentUrl);
      console.warn('[AuthConfig]   Normalized configured:', normalizedRedirect);
      console.warn('[AuthConfig]   Normalized current:', normalizedCurrent);
      console.warn('[AuthConfig]   This may cause authentication failures if Azure AD redirects to current URL');
    } else {
      console.log('[AuthConfig] ✅ Redirect URI matches current URL');
    }
    
    return redirectUri;
  }
  // Fallback: use root path with trailing slash if no environment variable
  const currentOrigin = window.location.origin;
  const redirectUri = `${currentOrigin}/`;
  console.log('[AuthConfig] Using current origin as redirect URI:', redirectUri);
  return redirectUri;
};

const redirectUri = getRedirectUri();

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || import.meta.env.VITE_ENTRA_EXTERNAL_ID_CLIENT_ID,
    authority: import.meta.env.VITE_AZURE_AD_AUTHORITY || import.meta.env.VITE_ENTRA_EXTERNAL_ID_AUTHORITY || 'https://login.microsoftonline.com/your-tenant-id',
    knownAuthorities: [import.meta.env.VITE_AZURE_AD_KNOWN_AUTHORITIES || import.meta.env.VITE_ENTRA_EXTERNAL_ID_KNOWN_AUTHORITIES || 'login.microsoftonline.com'],
    redirectUri: redirectUri, // Explicitly set - must match Azure AD registration exactly
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false, // Important: prevents MSAL from using current URL as redirect URI
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
    // Ensure state is preserved across redirects
    secureCookies: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
        }
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

export const tokenRequest = {
  scopes: ['openid', 'profile', 'email'],
};

// API token request to get access tokens for the backend API
// Extract API client ID from VITE_API_SCOPE or use VITE_AZURE_AD_API_CLIENT_ID
const getApiClientId = (): string => {
  // If VITE_AZURE_AD_API_CLIENT_ID is set, use it directly
  if (import.meta.env.VITE_AZURE_AD_API_CLIENT_ID) {
    const clientId = import.meta.env.VITE_AZURE_AD_API_CLIENT_ID;
    console.log('[AuthConfig] Using API client ID from VITE_AZURE_AD_API_CLIENT_ID:', clientId);
    return clientId;
  }
  
  // If VITE_API_SCOPE is set, extract client ID from it (format: api://{client-id}/access_as_user)
  if (import.meta.env.VITE_API_SCOPE) {
    const scopeMatch = import.meta.env.VITE_API_SCOPE.match(/api:\/\/([^/]+)\//);
    if (scopeMatch && scopeMatch[1]) {
      const clientId = scopeMatch[1];
      console.log('[AuthConfig] Extracted API client ID from VITE_API_SCOPE:', clientId);
      console.log('[AuthConfig] Full VITE_API_SCOPE:', import.meta.env.VITE_API_SCOPE);
      return clientId;
    } else {
      console.warn('[AuthConfig] ⚠️ VITE_API_SCOPE found but could not extract client ID:', import.meta.env.VITE_API_SCOPE);
    }
  }
  
  // Fallback to default
  const fallbackClientId = 'a93d6766-fc03-433b-888c-e38d627fe63d';
  console.warn('[AuthConfig] ⚠️ Using fallback API client ID:', fallbackClientId);
  console.warn('[AuthConfig] ⚠️ VITE_AZURE_AD_API_CLIENT_ID is not set');
  console.warn('[AuthConfig] ⚠️ VITE_API_SCOPE is not set or invalid');
  return fallbackClientId;
};

const apiClientId = getApiClientId();
const apiScope = `api://${apiClientId}/access_as_user`;

export const apiTokenRequest = {
  scopes: [apiScope],
};

// Log the configured API scope for debugging
console.log('[AuthConfig] Configured API scope:', apiScope);

// Fallback to ID token for API calls (simpler for Azure AD B2B)
export const idTokenRequest = {
  scopes: ['openid', 'profile', 'email'],
};


